import { NavLink, useLocation } from 'react-router-dom';
import { alsoActive } from '@/app/shells/cabinet/unlistedPages';
import type { CabinetSidebarProps } from '@/app/shells/cabinet/types/CabinetSidebarProps';
import type { NavItem } from '@/app/shells/cabinet/types/NavItem';
import { Icon } from '@/features/common/Icon';
import { appShell } from '@/i18n/dictionaries/appShell';
import { useTranslation } from '@/i18n/useTranslation';
import '@/app/shells/cabinet/cabinet-sidebar.css';

/**
 * The cabinet sidebar — the drawer on narrow screens, with the overlay behind it — and its two
 * sections: the user's own pages, then "Administrare" when the session may see any of it.
 */
export function CabinetSidebar({
  items,
  adminItems,
  drawerOpen,
  drawerRef,
  onOverlayClick,
  onCloseClick,
}: CabinetSidebarProps) {
  const { t } = useTranslation(appShell);
  const { pathname } = useLocation();

  const renderLinks = (list: NavItem[]) =>
    list.map((i) => (
      <li className="sidebar__item" key={i.to}>
        <NavLink
          to={i.to}
          end={i.exact}
          className={({ isActive }) =>
            'sidebar__link' + (isActive || alsoActive(i, pathname) ? ' sidebar__link--active' : '')
          }
        >
          <Icon name={i.icon} className="sidebar__icon" />
          <span className="sidebar__label">{t(i.label)}</span>
        </NavLink>
      </li>
    ));

  return (
    <>
      {drawerOpen && <div className="drawer-overlay" onClick={onOverlayClick} aria-hidden="true" />}
      <aside
        ref={drawerRef}
        id="cabinet-sidebar"
        className={'sidebar' + (drawerOpen ? ' sidebar--open' : '')}
        aria-label={t('sidebarNavAria')}
      >
        {drawerOpen && (
          <button
            type="button"
            className="drawer-close"
            aria-label={t('closeMenuAria')}
            onClick={onCloseClick}
          >
            <Icon name="cross-large" className="drawer-close__icon" />
          </button>
        )}
        <nav className="sidebar__nav">
          <section className="sidebar__section">
            <ul className="sidebar__list">{renderLinks(items)}</ul>
          </section>
          {adminItems.length > 0 && (
            <section className="sidebar__section sidebar__section--admin">
              <div className="sidebar__heading">
                <hr className="sidebar__divider" />
                <h2 className="sidebar__title">{t('administration')}</h2>
              </div>
              <ul className="sidebar__list">{renderLinks(adminItems)}</ul>
            </section>
          )}
        </nav>
      </aside>
    </>
  );
}
